/**
 * Layer 3 — Impressions. What a specific NPC believes (§11). Separate from
 * truth, per NPC. Marco doesn't know what happened — he knows what he saw
 * and what he was told.
 *
 * Two tiers, one rule each:
 *
 *  - Permanent. Trigger is categorical, never a threshold: violence,
 *    betrayal, a discovered lie, help when exposed, their flaw, their bonds.
 *    Always retrieves, never ages out.
 *  - Decaying. Fades without reinforcement. The same thing happening again
 *    reinforces it; "twice, and it promotes" (§11). Patterns matter more
 *    than incidents.
 *
 * Fading is lazy: a decaying impression is faded when `fadesOn` has passed,
 * whether or not anything has pruned it yet (§10).
 */

import { actorTag, entityTag, mintImpressionId } from '../../sim/ids.js';
import { addMinutes, compareTime, isSameOrAfter, MINUTES_PER_DAY } from '../../sim/time.js';
import { TUNING } from '../../sim/tuning.js';
import type {
  Actor,
  BelievedEvent,
  EntityRef,
  Event,
  EventId,
  Impression,
  ImpressionSource,
  MemoryTrigger,
  NPC,
  NpcId,
  WorldState,
  WorldTime,
} from '../../sim/types/index.js';
import { isViolent } from './events.js';

const MEMORY_TRIGGERS: ReadonlySet<string> = new Set<MemoryTrigger>([
  'violence',
  'betrayal',
  'lie_discovered',
  'help_when_exposed',
  'flaw',
  'bond',
]);

export function isMemoryTrigger(value: unknown): value is MemoryTrigger {
  return typeof value === 'string' && MEMORY_TRIGGERS.has(value);
}

function refIsActor(ref: EntityRef | null, actor: Actor): boolean {
  if (ref === null) return false;
  return actor === 'player' ? ref.kind === 'player' : ref.kind === 'npc' && ref.id === actor;
}

function refIsBonded(ref: EntityRef | null, npc: NPC): boolean {
  if (ref === null || ref.kind !== 'npc') return false;
  return npc.bonds.includes(ref.id);
}

const TOUCHES_A_PERSON: ReadonlySet<string> = new Set([
  'fistfight',
  'shakedown',
  'robbery',
  'hit',
  'gunfight',
  'interrogation',
  'death',
  'arrest',
  'departure',
  'lie_discovered',
  'secret_exposed',
  'appointment_missed',
  'obligation_lapsed',
]);

/**
 * The categorical rule (§11). Given who is remembering and what happened,
 * either a permanent trigger or null for the decay tier.
 *
 * Only what the event's structure can prove is classified here. `betrayal`,
 * `help_when_exposed` and `flaw` need a judgment the event shape does not
 * carry, so they arrive as explicit `memory` outcomes (§14) from the scene
 * machinery — this function never guesses at them. A missed meeting or a
 * lapsed debt is the decaying case by the spec's own example ("stiffed on a
 * small payment — remembered for weeks"), and promotes on repeat.
 */
export function classifyPermanence(npc: NPC, event: Event): MemoryTrigger | null {
  const doneToThem = refIsActor(event.target, npc.id);
  const doneToTheirs = refIsBonded(event.target, npc) || (event.actor !== 'player' && npc.bonds.includes(event.actor));
  const witnessed = event.witnesses.includes(npc.id);

  // Violence — witnessed, or done to them or theirs.
  if (isViolent(event.type) && (witnessed || doneToThem || doneToTheirs)) return 'violence';

  // Being lied to, once discovered.
  if (event.type === 'lie_discovered' && (doneToThem || witnessed)) return 'lie_discovered';

  // Anything touching a person they're bonded to. A chat with their sister
  // is not touching her; a fight, an arrest, a lapse, a departure is.
  if (doneToTheirs && TOUCHES_A_PERSON.has(event.type)) return 'bond';

  return null;
}

/** Tags an impression is retrievable by (§19). */
export function tagsForEvent(event: Event): string[] {
  const tags = new Set<string>([`type:${event.type}`, actorTag(event.actor)]);
  if (event.target) tags.add(entityTag(event.target));
  if (event.location) tags.add(`location:${event.location}`);
  if (isViolent(event.type)) tags.add('violence');
  if (event.type === 'transfer') tags.add('money');
  if (event.type === 'appointment_kept' || event.type === 'appointment_missed' || event.type === 'obligation_lapsed') {
    tags.add('agreement');
  }
  return [...tags].sort();
}

export function believedFrom(event: Event): BelievedEvent {
  return {
    type: event.type,
    actor: event.actor,
    target: event.target,
    location: event.location,
    at: event.at,
  };
}

/**
 * What makes two incidents "the same thing again": same kind of act, by the
 * same person, to the same target. Location and time are allowed to differ.
 */
export function patternKey(believed: BelievedEvent): string {
  const actor = believed.actor === null ? '?' : actorTag(believed.actor);
  const target = believed.target === null ? '?' : entityTag(believed.target);
  return `${believed.type ?? '?'}|${actor}|${target}`;
}

/** Events too routine to become a pattern worth remembering forever. */
const NEVER_PROMOTES: ReadonlySet<string> = new Set(['conversation']);

export function isFaded(impression: Impression, now: WorldTime): boolean {
  return impression.permanence.tier === 'decaying' && isSameOrAfter(now, impression.permanence.fadesOn);
}

export interface FormImpressionOptions {
  readonly origin: EventId | null;
  readonly source: ImpressionSource;
  /** Force the permanent tier with this reason (a `memory` outcome, §14). */
  readonly trigger?: MemoryTrigger;
  readonly tags?: readonly string[];
}

export interface FormedImpression {
  readonly state: WorldState;
  readonly impression: Impression;
  /** True if this write reinforced an existing decaying memory instead of adding one. */
  readonly reinforced: boolean;
  /** True if the write promoted a decaying memory to permanent. */
  readonly promoted: boolean;
}

/**
 * Write what one NPC now believes. Applies the tier rules: a categorical
 * trigger makes it permanent outright; otherwise it reinforces a matching
 * unfaded memory (promoting on the second occurrence) or starts a new
 * decaying one.
 */
export function formImpression(
  state: WorldState,
  npcId: NpcId,
  believed: BelievedEvent,
  options: FormImpressionOptions,
  now: WorldTime = state.time,
): FormedImpression {
  const tags = options.tags ? [...options.tags].sort() : [];
  const key = patternKey(believed);

  if (options.trigger === undefined) {
    const existing = Object.values(state.memory.impressions).find(
      (imp) =>
        imp.npc === npcId &&
        imp.permanence.tier === 'decaying' &&
        !isFaded(imp, now) &&
        patternKey(imp.believed) === key,
    );
    if (existing && existing.permanence.tier === 'decaying') {
      const reinforcements = existing.permanence.reinforcements + 1;
      const promote =
        !NEVER_PROMOTES.has(believed.type ?? '') &&
        reinforcements >= TUNING.memory.promoteAfterReinforcements;
      const mergedTags = [...new Set([...existing.tags, ...tags])].sort();
      const updated: Impression = {
        ...existing,
        // The pattern's record is its most recent instance.
        believed,
        origin: options.origin,
        source: options.source,
        formedOn: now,
        tags: mergedTags,
        permanence: promote
          ? { tier: 'permanent', trigger: 'pattern' }
          : { tier: 'decaying', fadesOn: fadeTime(now), reinforcements },
      };
      return {
        state: {
          ...state,
          memory: {
            ...state.memory,
            impressions: { ...state.memory.impressions, [updated.id]: updated },
          },
        },
        impression: updated,
        reinforced: true,
        promoted: promote,
      };
    }
  }

  const minted = mintImpressionId(state);
  const impression: Impression = {
    id: minted.id,
    npc: npcId,
    believed,
    origin: options.origin,
    source: options.source,
    formedOn: now,
    permanence:
      options.trigger === undefined
        ? { tier: 'decaying', fadesOn: fadeTime(now), reinforcements: 0 }
        : { tier: 'permanent', trigger: options.trigger },
    tags,
  };
  return {
    state: {
      ...minted.state,
      memory: {
        ...minted.state.memory,
        impressions: { ...minted.state.memory.impressions, [impression.id]: impression },
      },
    },
    impression,
    reinforced: false,
    promoted: false,
  };
}

function fadeTime(from: WorldTime): WorldTime {
  return addMinutes(from, TUNING.memory.decayDays * MINUTES_PER_DAY);
}

/** Drop every decaying impression whose time has passed. */
export function pruneFaded(state: WorldState, now: WorldTime = state.time): WorldState {
  const kept: Record<string, Impression> = {};
  let dropped = 0;
  for (const imp of Object.values(state.memory.impressions)) {
    if (isFaded(imp, now)) dropped += 1;
    else kept[imp.id] = imp;
  }
  if (dropped === 0) return state;
  return { ...state, memory: { ...state.memory, impressions: kept } };
}

/** Everything one NPC currently holds, unfaded, newest first. */
export function impressionsOf(state: WorldState, npcId: NpcId, now: WorldTime = state.time): Impression[] {
  return Object.values(state.memory.impressions)
    .filter((imp) => imp.npc === npcId && !isFaded(imp, now))
    .sort((x, y) => compareTime(y.formedOn, x.formedOn));
}
